import { FC, useContext, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Text,
  Modal,
  Stack,
  Paper,
  Group,
  Title,
  Anchor,
  Button,
  Divider,
  createStyles,
} from "@mantine/core";

import { Form } from "@components/Form/Form";
import { FormProvider } from "@components/FormProvider/FormProvider";
import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

import { Step1 as VolunteeringStep1 } from "@routes/VolunteeringFormRoute/components/Step1";
import { Step2 as VolunteeringStep2 } from "@routes/VolunteeringFormRoute/components/Step2";

import { Step1 as DemoRegistrationStep1 } from "@routes/DemoRegistrationFormRoute/components/Step1";
import { Step2 as DemoRegistrationStep2 } from "@routes/DemoRegistrationFormRoute/components/Step2";

import { Step1 as EventRegistrationStep1 } from "@routes/EventRegistrationFormRoute/components/Step1";

import { ReactComponent as VolunteeringSvg } from "@assets/EhrenamtCP.svg";
import { ReactComponent as DemoRegistrationSvg } from "@assets/DemoEP.svg";
import { ReactComponent as EventRegistrationSvg } from "@assets/Event.svg";

import { Draft } from "@utils/Draft.util";
import { emailAtom } from "../atoms/email.atom";
import FORM_DETAILS from "@jsons/FORM_DETAILS.json";
import { useLanguageCode } from "@hooks/useLanguageCode";

import { IDemoRegistrationForm } from "@models/DemoRegistrationForm.model";
import { IEventRegistrationForm } from "@models/EventRegistrationForm.model";
import { IIVolunteeringFormData } from "@routes/VolunteeringFormRoute/VolunteeringFormRoute.types";

type FormName = keyof typeof FORM_DETAILS;

const useStyles = createStyles((t) => ({
  cell: {
    display: "flex",
    gap: t.spacing.sm,
    padding: t.spacing.lg,
    flexDirection: "column",
    alignItems: "flex-start",
  },
  form: { paddingTop: "unset" },
  formIcon: {
    width: 32,
    height: 32,
    marginBottom: t.spacing.lg,
  },
  formTitle: {
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export const FormCard: FC<{ form: FormName }> = ({ form }) => {
  const email = useAtomValue(emailAtom);
  const [isOpened, setIsOpened] = useState(false);

  const {
    user,
    authUser: { uid },
  } = useContext(AuthContext)!;

  const { classes } = useStyles();

  const lc = useLanguageCode();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const [Icon, formName, readableFormName] = useMemo(() => {
    switch (form) {
      case "Volunteering":
        return [VolunteeringSvg, "volunteering", "Volunteering"];
      case "DemoRegistration":
        return [
          DemoRegistrationSvg,
          "demo-registration",
          "Demonstration Registration",
        ];
      case "EventRegistration":
        return [
          EventRegistrationSvg,
          "event-registration",
          "Event Registration",
        ];
    }
  }, [form]);

  const subject = t("Request for change {{form}}", {
    form: t(readableFormName),
  });
  const bodyEnglish = `
I am writing to request changes to the form I recently submitted to you. I would like to make changes or corrections. 
%0D%0A
%0D%0A
The changes I would like to request are as follows:
%0D%0A
%0D%0A
[List of specific changes that need to be made]
%0D%0A
%0D%0A
%0D%0A
%0D%0A
Sincerely,
%0D%0A
${user.firstName} ${user.lastName}
%0D%0A
${user.email}
  `;
  const bodyGerman = `
Ich schreibe Ihnen, um Änderungen an dem Formular zu beantragen, das ich kürzlich bei Ihnen eingereicht habe. Ich würde gerne Änderungen oder Korrekturen vornehmen. 
%0D%0A
%0D%0A
Die Änderungen, die ich beantragen möchte, sind folgende:
%0D%0A
%0D%0A
[Liste der spezifischen Änderungen, die vorgenommen werden müssen]
%0D%0A
%0D%0A
%0D%0A
%0D%0A
Mit freundlichen Grüßen,
%0D%0A
${user.firstName} ${user.lastName}
%0D%0A
${user.email}
`;

  const bodies = {
    de: bodyGerman,
    en: bodyEnglish,
  };

  const body = bodies[lc as keyof typeof bodies];

  const now = dayjs();

  const canSubmit =
    !FORM_DETAILS[form].dueDate ||
    now.isBefore(dayjs(FORM_DETAILS[form].dueDate));

  return (
    <Paper className={classes.cell}>
      <FormProvider uid={uid} formName={formName} ignoreSubmission={true}>
        {({ isSubmitted, draft }) => (
          <>
            <Icon className={classes.formIcon} />
            <Title order={2} sx={(t) => ({ height: 70.2 })}>
              {t(FORM_DETAILS[form].title)}{" "}
              {form === "Volunteering" && t("Form")}
            </Title>
            <Text color="dimmed" size="sm" style={{ flex: 1 }}>
              {t(
                canSubmit
                  ? FORM_DETAILS[form].description
                  : FORM_DETAILS[form].ended
              )}
            </Text>
            <Group spacing="xs">
              {canSubmit ? (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/forms/${formName}`)}
                >
                  {t(isSubmitted ? "cards.labels.view" : "cards.labels.submit")}
                </Button>
              ) : (
                <>
                  {!!draft && (
                    <>
                      <Anchor size="sm" onClick={() => setIsOpened(true)}>
                        {t("View Submission")}
                      </Anchor>
                      <Divider
                        h={12}
                        orientation="vertical"
                        sx={{ alignSelf: "center" }}
                      />
                      <Anchor
                        size="sm"
                        href={`mailto:${email}?subject=${subject}&body=${body}`}
                      >
                        {t("Request Changes")}
                      </Anchor>
                    </>
                  )}
                </>
              )}
            </Group>
            <Modal
              size={700}
              onClose={() => {
                setIsOpened(false);
              }}
              opened={isOpened}
            >
              <Form
                key={isOpened ? 1 : 0}
                totalSteps={2}
                currentStep={1}
                isSaving={false}
                showSteps={false}
                showHeader={false}
                className={classes.form}
                title={FORM_DETAILS[form].title}
                description={FORM_DETAILS[form].description}
              >
                <Stack>
                  {form === "Volunteering" && (
                    <>
                      <VolunteeringStep1
                        readonly
                        user={user}
                        draft={draft as Draft<IIVolunteeringFormData>}
                      />
                      <Divider />
                      <VolunteeringStep2
                        readonly
                        draft={draft as Draft<IIVolunteeringFormData>}
                      />
                    </>
                  )}

                  {form === "DemoRegistration" && (
                    <>
                      <DemoRegistrationStep1
                        readonly
                        draft={draft as Draft<IDemoRegistrationForm>}
                      />
                      <DemoRegistrationStep2
                        readonly
                        draft={draft as Draft<IDemoRegistrationForm>}
                      />
                    </>
                  )}

                  {form === "EventRegistration" && (
                    <>
                      <EventRegistrationStep1
                        readonly
                        draft={draft as Draft<IEventRegistrationForm>}
                      />
                    </>
                  )}
                </Stack>
              </Form>
            </Modal>
          </>
        )}
      </FormProvider>
    </Paper>
  );
};
